import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import {
  Title,
  useNotify,
} from "react-admin";
import {
  Box,
  Grid,
  Typography,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
  MenuItem,
  Select,
  TextField as MuiTextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { alpha } from "@mui/material/styles";

// Custom components
import {
  SummaryCard,
  SafePieChart,
  StatusField,
} from "../../components/reports/teerab";

// List components (adjust paths according to your project)
import { OfferList } from "../../Offers";
import ServiceList from "../../Services";
import InputProductList from "../../InputProducts";

// Helper
const formatSDG = (amount) => {
  if (!amount) return "0 SDG";
  amount = Number(amount);
  if (isNaN(amount)) return "0 SDG";
  if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M SDG`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(1)}K SDG`;
  return `${amount.toLocaleString()} SDG`;
};

// Type-specific dashboard (inner component)
const TypeDashboard = ({ type }) => {
  const notify = useNotify();
  const theme = useTheme();

  // Mapping for API endpoints and field names – memoized to avoid recreation
  const config = useMemo(() => {
    const typeConfig = {
      crops: {
        endpoint: "offers",
        typeField: "subCategory_id",
        typeEndpoint: "sub_category",
        listComponent: OfferList,
        resource: "offers",
        defaultFilter: { offer_type: "buy" },
      },
      services: {
        endpoint: "services",
        typeField: "service_id",
        typeEndpoint: "servicetypes",
        listComponent: ServiceList,
        resource: "services",
        defaultFilter: { service_type: "demand" },
      },
      inputs: {
        endpoint: "input-products",
        typeField: "category_id",
        typeEndpoint: "inputs",
        listComponent: InputProductList,
        resource: "input-products",
        defaultFilter: { input_type: "buy" },
      },
    };
    return typeConfig[type];
  }, [type]);

  // Filter states
  const [selectedState, setSelectedState] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [gender, setGender] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Meta data
  const [states, setStates] = useState([]);
  const [types, setTypes] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  // Report data
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Inner tab (Overview / List)
  const [innerTab, setInnerTab] = useState(0);

  const token = JSON.parse(localStorage.getItem("token"));

  // Fetch states and types
  useEffect(() => {
    const fetchMeta = async () => {
      setLoadingMeta(true);
      try {
        // States (common)
        const stateRes = await fetch(
          `${process.env.REACT_APP_URL}/admin/state`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );
        const statesData = await stateRes.json();
        setStates(statesData);

        // Types (dynamic)
        const typesRes = await fetch(
          `${process.env.REACT_APP_URL}/admin/${config.typeEndpoint}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: "Bearer " + token,
            },
          }
        );
        const typesData = await typesRes.json();
        setTypes(typesData["data"] || []);
      } catch (err) {
        console.error("Error fetching meta data:", err);
        notify("Error loading filters", { type: "warning" });
      } finally {
        setLoadingMeta(false);
      }
    };
    fetchMeta();
  }, [config.typeEndpoint, token, notify]);

  // Fetch report data when filters change
  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      try {
        if (selectedState)
          params.append("filter[]", `state||$eq||${selectedState}`);
        if (selectedType)
          params.append("filter[]", `${config.typeField}||$eq||${selectedType}`);
        if (gender) params.append("filter[]", `gender||$eq||${gender}`);
        if (startDate)
          params.append("filter[]", `created_at_gte||$cont||${startDate}`);
        if (endDate)
          params.append("filter[]", `created_at_lte||$cont||${endDate}`);
        if (type === 'crops') {
          params.append("filter[]", `offer_type||$cont||buy`);
        } else if (type === 'services') {
          params.append("filter[]", `service_type||$cont||demand`);
        } else if (type === 'inputs') {
          params.append("filter[]", `input_type||$cont||buy`);
        }
        const url =
          `${process.env.REACT_APP_URL}/admin/${config.endpoint}` +
          (params.toString() ? `?${params.toString()}` : "");

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setReportData(data);
      } catch (err) {
        console.error("Error fetching report data:", err);
        setError(err);
        notify("Error loading report data", { type: "error" });
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [selectedState, selectedType, gender, startDate, endDate, config, token, notify, type]);

  const genders = ["male", "female"];

  if (loadingMeta) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Filter Bar */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3} md={2}>
          <Typography variant="subtitle2">State</Typography>
          <Select
            fullWidth
            value={selectedState}
            onChange={(e) => setSelectedState(e.target.value)}
            displayEmpty
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            {states.map((state) => (
              <MenuItem key={state.id} value={state.id}>
                {state.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="subtitle2">Type</Typography>
          <Select
            fullWidth
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            displayEmpty
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            {types.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <Typography variant="subtitle2">Gender</Typography>
          <Select
            fullWidth
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            displayEmpty
            size="small"
          >
            <MenuItem value="">Any</MenuItem>
            {genders.map((g) => (
              <MenuItem key={g} value={g}>
                {g}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <Typography variant="subtitle2">From</Typography>
          <DatePicker
            value={startDate || null}
            onChange={(newDate) => {
              const iso = newDate ? newDate.toISOString().slice(0, 10) : "";
              setStartDate(iso);
            }}
            renderInput={(params) => (
              <MuiTextField
                {...params}
                fullWidth
                size="small"
                inputProps={{ ...params.inputProps, readOnly: true }}
              />
            )}
          />
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <Typography variant="subtitle2">To</Typography>
          <DatePicker
            value={endDate || null}
            onChange={(newDate) => {
              const iso = newDate ? newDate.toISOString().slice(0, 10) : "";
              setEndDate(iso);
            }}
            renderInput={(params) => (
              <MuiTextField
                {...params}
                fullWidth
                size="small"
                inputProps={{ ...params.inputProps, readOnly: true }}
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Inner Tabs (Overview / List) */}
      <Box sx={{ mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: "blur(8px)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Tabs
            value={innerTab}
            onChange={(e, newValue) => setInnerTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                fontWeight: "600",
                textTransform: "none",
                fontSize: "0.875rem",
              },
            }}
          >
            <Tab label="Overview" />
            <Tab label="List" />
          </Tabs>
        </Paper>
      </Box>

      {/* Content */}
      {loading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ my: 3 }}>
          Failed to load data. {error.message}
        </Alert>
      )}

      {!loading && !error && reportData && innerTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <SafePieChart
              data={reportData["typeDistribution"]}
              title="Type Distribution"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SafePieChart
              data={reportData["userDemographics"]?.gender}
              title="Gender Distribution"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SafePieChart
              data={reportData["userDemographics"]?.topLocations}
              title="Location Distribution"
            />
          </Grid>
        </Grid>
      )}

      {!loading && !error && innerTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {config.listComponent && (
              <config.listComponent
                resource={config.resource}
                filter={config.defaultFilter}
              />
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

// Main component with outer tabs
const Requests = () => {
  const theme = useTheme();
  const [outerTab, setOuterTab] = useState(0); // 0: Crops, 1: Services, 2: Inputs

  const outerTabs = [
    { label: "Crops", value: "crops" },
    { label: "Services", value: "services" },
    { label: "Inputs", value: "inputs" },
  ];

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.background.default,
          0.9
        )} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
        p: 3,
        borderRadius: "16px",
      }}
    >
      <Title title="Teerab Requests" />

      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          fontWeight="800"
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            display: "inline-block",
          }}
        >
          Requests Report
        </Typography>
      </Box>

      {/* Outer Tabs */}
      <Box sx={{ mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: "blur(8px)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Tabs
            value={outerTab}
            onChange={(e, newValue) => setOuterTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                fontWeight: "600",
                textTransform: "none",
                fontSize: "1rem",
              },
            }}
          >
            {outerTabs.map((tab, index) => (
              <Tab key={tab.value} label={tab.label} />
            ))}
          </Tabs>
        </Paper>
      </Box>

      {/* Render active type dashboard */}
      <TypeDashboard type={outerTabs[outerTab].value} />
    </Box>
  );
};

export default Requests;